import {
  getVendorStats,
  getVendorDashboard,
  postVendorProperties,
  getTopSellingProperties,
  getVendorBooking,
  getTopSellingRooms,
  getVendorPropertyStats,
  getVendorPropertiesTaxesFees,
  getVendorPropertyTaxesFees,
  getPropertyTypes,
  getRoomTypes,
  getBedTypes,
  postVendorPropertiesPolicies,
  postVendorPropertyTaxesFees,
  postVendorPropertyServices,
  postVendorPropertyRoom,
  postVendorPropertyRatePlan,
  postVendorPropertyRoomImages,
  postVendorPropertiesAmenities,
  getVendorPropertiesAmenities,
  getRoomAmenities,
  postVendorRoomAmenities,
  getVendorProperties,
  getVendorPropertyCompletionStatus,
  getVendorPropertyServices,
  getVendorPropertyContract,
  postVendorPropertyCompleteSubmission,
  getVendorActivePropertyStatus,
  getVendorPropertyTodayActivity,
  setRoomAvailability,
  setPropertyRoomsAvailabilityBulk,
  getRoomAvailability,
  getPropertyRoomsAvailability,
  getPropertyImages,
  postVendorPropertyImages,
  deletePropertyImage,
  getPropertyAmenitiesDetail,
  getPropertyRoomsList,
  getRoomAmenitiesDetail,
  getRoomBeds,
  postVendorRoomBeds,
  putVendorRoomBeds,
  deleteVendorRoomBeds,
  getVendorProperty,
  putVendorProperty,
  putVendorPropertiesPolicies,
  putVendorPropertiesAmenities,
  putVendorPropertyServices,
  putVendorPropertyTaxesFees,
  putVendorPropertyContract,
  putVendorPropertyRoom,
  putVendorRoomAmenities,
  requestBookingCancel,
  requestBookingDateChange,
  confirmPayAtHotelPayment,
} from "./../../helpers/backend_helper";
import {
  getVendorStatsSuccess,
  getVendorDashboardSuccess,
  postVendorPropertiesSuccess,
  apiError,
  setLoading,
  resetVendorFlag,
  resetPropertyCreation,
  getTopSellingPropertiesSuccess,
  getTopSellingRoomsSuccess,
  getVendorPropertyStatsSuccess,
  getVendorPropertyTaxesFeesSuccess,
  getVendorPropertyTaxesFeesDetailSuccess,
  getVendorPropertyServicesSuccess,
  getVendorPropertyContractSuccess,
  getVendorPropertyCompletionStatusSuccess,
  getVendorActiveStatusSuccess,
  postVendorPropertiesAmenitiesSuccess,
  getVendorPropertiesAmenitiesSuccess,
  getRoomAmenitiesSuccess,
  getPropertyTypesSuccess,
  setPropertyTypesLoading,
  getRoomTypesSuccess,
  getBedTypesSuccess,
  postVendorRoomAmenitiesSuccess,
  postVendorPropertiesPoliciesSuccess,
  getAllVendorPropertiesSuccess,
  getVendorPropertySuccess,
} from "./reducer";

const extractApiErrorMessage = (error: any, fallback: string): string => {
  const source = error?.response?.data ?? error?.response ?? error;

  if (source?.errors && typeof source.errors === "object") {
    const fieldMessages = Object.keys(source.errors)
      .flatMap((key) => {
        const value = source.errors[key];
        if (Array.isArray(value)) return value;
        if (typeof value === "string") return [value];
        return [];
      })
      .filter((message): message is string => typeof message === "string" && message.trim().length > 0);

    if (fieldMessages.length > 0) {
      return Array.from(new Set(fieldMessages)).join(" ");
    }
  }

  if (typeof source?.error === "string" && source.error.trim().length > 0) {
    return source.error;
  }

  if (Array.isArray(source?.error)) {
    const messages = source.error.filter((message: any) => typeof message === "string" && message.trim().length > 0);
    if (messages.length > 0) return Array.from(new Set(messages)).join(" ");
  }

  if (typeof source?.message === "string" && source.message.trim().length > 0) {
    return source.message;
  }

  if (typeof error?.message === "string" && error.message.trim().length > 0) {
    return error.message;
  }

  return fallback;
};

export const MAX_IMAGES_PER_UPLOAD = 10;

const toTitleCase = (value: string): string =>
  value
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const toPropertyTypeValue = (value: string): string =>
  value
    .replace(/&/g, " and ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const extractPropertyTypeList = (response: any): any[] => {
  const candidates = [
    response?.data?.data,
    response?.data?.property_types,
    response?.data?.types,
    response?.property_types,
    response?.types,
    response?.data,
    response,
  ];

  const candidate = candidates.find((item) => Array.isArray(item) || (item && typeof item === "object"));
  if (Array.isArray(candidate)) return candidate;

  if (candidate && typeof candidate === "object") {
    return Object.entries(candidate).map(([value, label]) => ({ value, label }));
  }

  return [];
};

const normalizePropertyTypes = (response: any) => {
  const seen = new Set<string>();

  return extractPropertyTypeList(response)
    .filter((item: any) => item?.is_active !== false)
    .map((item: any) => {
      if (typeof item === "string" || typeof item === "number") {
        const rawValue = String(item).trim();
        return { label: toTitleCase(rawValue), value: toPropertyTypeValue(rawValue) };
      }

      const nameLike = item?.name ?? item?.label;
      const valueSource =
        item?.slug ??
        item?.value ??
        item?.code ??
        item?.key ??
        (nameLike ? toPropertyTypeValue(String(nameLike)) : item?.id);
      const labelSource = nameLike ?? valueSource;
      const value = String(valueSource ?? "").trim();
      const label = String(labelSource ?? "").trim() || toTitleCase(value);

      return { label, value };
    })
    .filter((option: any) => {
      if (!option.value || seen.has(option.value)) return false;
      seen.add(option.value);
      return true;
    });
};

export const fetchVendorStats = () => async (dispatch: any) => {
  try {
    dispatch(setLoading(true));

    const response: any = await getVendorStats();

    if (response && response.success) {
      dispatch(getVendorStatsSuccess(response.data));
    } else {
      dispatch(apiError("Failed to fetch vendor statistics"));
    }
  } catch (error: any) {
    const errorMessage = extractApiErrorMessage(error, "An error occurred while fetching vendor statistics");
    dispatch(apiError(errorMessage));
  }
};

export const fetchVendorDashboard = () => async (dispatch: any) => {
  try {
    dispatch(setLoading(true));

    const response: any = await getVendorDashboard();

    if (response && response.success) {
      dispatch(getVendorDashboardSuccess(response.data));
    } else {
      dispatch(apiError("Failed to fetch vendor dashboard"));
    }
  } catch (error: any) {
    const errorMessage = extractApiErrorMessage(error, "An error occurred while fetching vendor dashboard");
    dispatch(apiError(errorMessage));
  }
};

export const fetchTopSellingProperty = () => async (dispatch: any) => {
  try {
    dispatch(setLoading(true));

    const response: any = await getTopSellingProperties();

    if (response && response.success) {
      dispatch(getTopSellingPropertiesSuccess(response.data));
    } else {
      dispatch(apiError("Failed to fetch properties "));
    }
  } catch (error: any) {
    const errorMessage = extractApiErrorMessage(error, "An error occurred while fetching vendor properties");
    dispatch(apiError(errorMessage));
  }
};
export const fetchAllVendorProperty = (params?: Record<string, any>) => async (dispatch: any) => {
  try {
    dispatch(setLoading(true));

    const response: any = await getVendorProperties(params);

    if (response && response.success) {
      dispatch(getAllVendorPropertiesSuccess({ data: response.data, meta: response.meta }));
    } else {
      dispatch(apiError("Failed to fetch All properties "));
    }
  } catch (error: any) {
    const errorMessage = extractApiErrorMessage(error, "An error occurred while fetching all vendor properties");
    dispatch(apiError(errorMessage));
  }
};

export const hasExistingVendorProperty = (payload: any): boolean => {
  const total =
    payload?.meta?.total ??
    payload?.data?.meta?.total ??
    payload?.pagination?.total ??
    payload?.data?.pagination?.total ??
    payload?.total ??
    payload?.data?.total;

  if (Number(total) > 0) return true;

  const candidates = [
    payload?.data?.properties,
    payload?.properties,
    payload?.data?.data,
    payload?.data,
    payload,
  ];

  return candidates.some((candidate) => Array.isArray(candidate) && candidate.length > 0);
};

export const fetchVendorExistingPropertyStatus = () => async (dispatch: any) => {
  try {
    dispatch(setLoading(true));

    const response: any = await getVendorProperties({ page: 1, per_page: 1 });

    if (response && response.success) {
      dispatch(getAllVendorPropertiesSuccess({ data: response.data, meta: response.meta }));
      return {
        success: true,
        data: response.data,
        meta: response.meta,
        hasExistingProperty: hasExistingVendorProperty(response),
      };
    }

    const errorMessage = extractApiErrorMessage(response, "Failed to fetch vendor properties");
    dispatch(apiError(errorMessage));
    return { success: false, error: errorMessage };
  } catch (error: any) {
    const errorMessage = extractApiErrorMessage(error, "An error occurred while fetching vendor properties");
    dispatch(apiError(errorMessage));
    return { success: false, error: errorMessage };
  }
};

export const fetchTopSellingRoom = () => async (dispatch: any) => {
  try {
    dispatch(setLoading(true));

    const response: any = await getTopSellingRooms();

    if (response && response.success) {
      dispatch(getTopSellingRoomsSuccess(response.data));
    } else {
      dispatch(apiError("Failed to fetch Room "));
    }
  } catch (error: any) {
    const errorMessage = extractApiErrorMessage(error, "An error occurred while fetching vendor Room");
    dispatch(apiError(errorMessage));
  }
};

interface PropertyStatsParams {
  page?: number;
  per_page?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  status?: string;
  search?: string;
}

export const fetchVendorPropertyStats = (params?: PropertyStatsParams) => async (dispatch: any) => {
  try {
    dispatch(setLoading(true));

    const response: any = await getVendorPropertyStats(params);

    if (response && response.success) {
      dispatch(getVendorPropertyStatsSuccess(response.data));
    } else {
      dispatch(apiError("Failed to fetch properties stats "));
    }
  } catch (error: any) {
    const errorMessage = extractApiErrorMessage(error, "An error occurred while fetching vendor properties stats");
    dispatch(apiError(errorMessage));
  }
};

export const fetchVendorPropertyAmenities = () => async (dispatch: any) => {
  try {
    dispatch(setLoading(true));

    const response: any = await getVendorPropertiesAmenities();

    if (response && response.success) {
      dispatch(getVendorPropertiesAmenitiesSuccess(response.data));
    } else {
      dispatch(apiError("Failed to fetch properties amenities "));
    }
  } catch (error: any) {
    const errorMessage = extractApiErrorMessage(error, "An error occurred while fetching vendor properties amenities");
    dispatch(apiError(errorMessage));
  }
};

export const fetchVendorPropertyTaxesFees = () => async (dispatch: any) => {
  try {
    dispatch(setLoading(true));

    const response: any = await getVendorPropertiesTaxesFees();

    if (response && response.success) {
      dispatch(getVendorPropertyTaxesFeesSuccess(response.data));
    } else {
      dispatch(apiError("Failed to fetch taxes and fees"));
    }
  } catch (error: any) {
    const errorMessage = extractApiErrorMessage(error, "An error occurred while fetching taxes and fees");
    dispatch(apiError(errorMessage));
  }
};

export const fetchVendorPropertyTaxesFeesDetail =
  (propertyId: number | string) => async (dispatch: any) => {
    try {
      dispatch(setLoading(true));

      const response: any = await getVendorPropertyTaxesFees(propertyId);

      if (response && response.success) {
        const data = response.data;
        dispatch(getVendorPropertyTaxesFeesDetailSuccess({ propertyId, data }));
        return { success: true, data };
      }

      const errorMessage = extractApiErrorMessage(response, "Failed to fetch property taxes and fees");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    } catch (error: any) {
      const errorMessage = extractApiErrorMessage(error, "An error occurred while fetching property taxes and fees");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };

export const fetchVendorPropertyServices =
  (propertyId: number | string) => async (dispatch: any) => {
    try {
      dispatch(setLoading(true));

      const response: any = await getVendorPropertyServices(propertyId);

      if (response && response.success) {
        const data = response.data;
        dispatch(getVendorPropertyServicesSuccess({ propertyId, data }));
        return { success: true, data };
      }

      const errorMessage = extractApiErrorMessage(response, "Failed to fetch property services");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    } catch (error: any) {
      const errorMessage = extractApiErrorMessage(error, "An error occurred while fetching property services");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };

export const fetchVendorPropertyContract =
  (propertyId: number | string) => async (dispatch: any) => {
    try {
      dispatch(setLoading(true));

      const response: any = await getVendorPropertyContract(propertyId);

      if (response && response.success) {
        const data = response.data;
        dispatch(getVendorPropertyContractSuccess({ propertyId, data }));
        return { success: true, data };
      }

      const errorMessage = extractApiErrorMessage(response, "Failed to fetch property agreement");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    } catch (error: any) {
      const errorMessage = extractApiErrorMessage(error, "An error occurred while fetching property agreement");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };

export const fetchVendorPropertyCompletionStatus =
  (propertyId: number | string, options?: { silent?: boolean }) => async (dispatch: any) => {
    const silent = options?.silent === true;
    try {
      if (!silent) {
        dispatch(setLoading(true));
      }

      const response: any = await getVendorPropertyCompletionStatus(propertyId);

      if (response && response.success) {
        dispatch(getVendorPropertyCompletionStatusSuccess(response.data));
        return { success: true, data: response.data };
      } else {
        const errorMessage = extractApiErrorMessage(response, "Failed to fetch property completion status");
        if (!silent) {
          dispatch(apiError(errorMessage));
        }
        return { success: false, error: errorMessage };
      }
    } catch (error: any) {
      const errorMessage = extractApiErrorMessage(error, "An error occurred while fetching property completion status");
      if (!silent) {
        dispatch(apiError(errorMessage));
      }
      return { success: false, error: errorMessage };
    }
  };

export const completeVendorPropertySubmission =
  (propertyId: number | string) => async (dispatch: any) => {
    try {
      const response: any = await postVendorPropertyCompleteSubmission(propertyId);

      if (response && response.success) {
        return { success: true, data: response.data, message: response?.message };
      }

    const errorMessage = extractApiErrorMessage(response, "Failed to complete property submission");
    dispatch(apiError(errorMessage));
    return { success: false, error: errorMessage };
  } catch (error: any) {
      const errorMessage = extractApiErrorMessage(error, "An error occurred while completing property submission");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };

// thunk for active property status (used during login)
export const fetchVendorActivePropertyStatus = () => async (dispatch: any) => {
  try {
    dispatch(setLoading(true));
    const response: any = await getVendorActivePropertyStatus();
    if (response && response.success) {
      // store it in state if needed
      dispatch(getVendorActiveStatusSuccess(response.data));
      return { success: true, data: response.data };
    } else {
      const errorMessage = extractApiErrorMessage(response, "Failed to fetch active property status");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    }
  } catch (error: any) {
    const errorMessage = extractApiErrorMessage(error, "An error occurred while fetching active property status");
    dispatch(apiError(errorMessage));
    return { success: false, error: errorMessage };
  }
};

export const fetchVendorProperty =
  (propertyId: number | string) => async (dispatch: any) => {
    try {
      const response: any = await getVendorProperty(propertyId);
      if (response?.success) {
        const property = response.data?.property ?? response.data;
        dispatch(getVendorPropertySuccess(property));
        return { success: true, data: property };
      }
      return { success: false, error: extractApiErrorMessage(response, "Failed to fetch property") };
    } catch (error: any) {
      return { success: false, error: extractApiErrorMessage(error, "Failed to fetch property") };
    }
  };

export const fetchPropertyImages =
  (propertyId: number | string) => async (_dispatch: any) => {
    try {
      const response: any = await getPropertyImages(propertyId);
      return { success: true, data: response };
    } catch (error: any) {
      return { success: false, error: extractApiErrorMessage(error, "Failed to fetch property images") };
    }
  };

export const uploadPropertyImages =
  ({ propertyId, files, photoType, tag, roomId }: any) => async (dispatch: any) => {
    try {
      if (files.length > MAX_IMAGES_PER_UPLOAD) {
        const errorMessage = `You can upload a maximum of ${MAX_IMAGES_PER_UPLOAD} images at a time.`;
        dispatch(apiError(errorMessage));
        return { success: false, error: errorMessage };
      }

      const formData = new FormData();
      files.forEach((file: File, index: number) => {
        formData.append(`images[${index}]`, file);
      });
      if (photoType) formData.append('photo_type', photoType);
      if (tag) formData.append('tag', tag);

      const response: any = roomId
        ? await postVendorPropertyRoomImages({ imageData: formData, propertyId, roomId })
        : await postVendorPropertyImages({ imageData: formData, propertyId });
      if (response?.success) return { success: true, data: response.data, message: response?.message };
      const errorMessage = extractApiErrorMessage(response, "Failed to upload photos");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    } catch (error: any) {
      const errorMessage = extractApiErrorMessage(error, "An error occurred while uploading photos");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };

export const removePropertyImage =
  ({ propertyId, imageId }: any) => async (dispatch: any) => {
    try {
      const response: any = await deletePropertyImage({ propertyId, imageId });
      if (response?.success) return { success: true, message: response?.message };
      const errorMessage = extractApiErrorMessage(response, "Failed to delete photo");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    } catch (error: any) {
      const errorMessage = extractApiErrorMessage(error, "An error occurred while deleting photo");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };

export const fetchPropertyAmenitiesDetail =
  (propertyId: number | string) => async (_dispatch: any) => {
    try {
      const response: any = await getPropertyAmenitiesDetail(propertyId);
      return { success: true, data: response };
    } catch (error: any) {
      return { success: false, error: extractApiErrorMessage(error, "Failed to fetch property amenities") };
    }
  };

export const fetchPropertyRoomsList =
  (propertyId: number | string) => async (_dispatch: any) => {
    try {
      const response: any = await getPropertyRoomsList(propertyId);
      return { success: true, data: response };
    } catch (error: any) {
      return { success: false, error: extractApiErrorMessage(error, "Failed to fetch property rooms") };
    }
  };

export const fetchRoomAmenitiesDetail =
  (roomId: number | string) => async (_dispatch: any) => {
    try {
      const response: any = await getRoomAmenitiesDetail(roomId);
      return { success: true, data: response };
    } catch (error: any) {
      return { success: false, error: extractApiErrorMessage(error, "Failed to fetch room amenities") };
    }
  };

export const fetchRoomAvailability =
  (roomId: number | string) => async (_dispatch: any) => {
    try {
      const response: any = await getRoomAvailability(roomId);
      return { success: true, data: response };
    } catch (error: any) {
      return { success: false, error: extractApiErrorMessage(error, 'Failed to fetch availability') };
    }
  };

export const fetchPropertyRoomsAvailability =
  (propertyId: number | string, params: { start_date: string; end_date: string }) =>
  async (_dispatch: any) => {
    try {
      const response: any = await getPropertyRoomsAvailability(propertyId, params);
      return { success: true, data: response };
    } catch (error: any) {
      return { success: false, error: extractApiErrorMessage(error, 'Failed to fetch property availability') };
    }
  };

export interface SingleRoomAvailabilityPayload {
  dates: string[];
  available: boolean;
  price?: number;
  min_stay?: number;
  max_stay?: number;
  inventory?: number;
  status?: "active" | "inactive";
}

export const saveRoomAvailability =
  (roomId: number | string, payload: SingleRoomAvailabilityPayload) => async (_dispatch: any) => {
    try {
      const response: any = await setRoomAvailability(roomId, payload);
      if (response && response.success) {
        return { success: true, data: response.data };
      }
      return { success: false, error: extractApiErrorMessage(response, "Failed to save availability") };
    } catch (error: any) {
      return { success: false, error: extractApiErrorMessage(error, "Failed to save availability") };
    }
  };

interface BulkAvailabilityPayload {
  start_date: string;
  end_date: string;
  room_ids?: number[];
  mode: "set" | "block" | "unblock";
  block_reason?: string;
  price?: number;
  inventory?: number;
  min_stay?: number;
  max_stay?: number;
}

export const savePropertyRoomsAvailabilityBulk =
  (propertyId: number | string, payload: BulkAvailabilityPayload) => async (_dispatch: any) => {
    try {
      const response: any = await setPropertyRoomsAvailabilityBulk(propertyId, payload);
      if (response && response.success) {
        return { success: true, data: response.data };
      }
      return { success: false, error: extractApiErrorMessage(response, "Failed to bulk update availability") };
    } catch (error: any) {
      return { success: false, error: extractApiErrorMessage(error, "Failed to bulk update availability") };
    }
  };

export const fetchPropertyTodayActivity =
  (propertyId: number | string) => async (_dispatch: any) => {
    try {
      const response: any = await getVendorPropertyTodayActivity(propertyId);
      return { success: true, data: response };
    } catch (error: any) {
      return { success: false, error: extractApiErrorMessage(error, "Failed to fetch today activity") };
    }
  };

export const fetchBookingDetail =
  (bookingId: number | string) => async (_dispatch: any) => {
    try {
      const response: any = await getVendorBooking(bookingId);
      if (response && response.success) {
        const bookings = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
            ? response.data.data
            : Array.isArray(response.data?.bookings)
              ? response.data.bookings
              : Array.isArray(response.data?.items)
                ? response.data.items
                : Array.isArray(response.bookings)
                  ? response.bookings
                  : null;

        const booking = bookings
          ? bookings.find((item: any) => String(item?.id) === String(bookingId)) ?? bookings[0]
          : response.data?.booking ?? response.booking ?? response.data;

        if (booking && typeof booking === "object" && Object.keys(booking).length > 0) {
          return { success: true, data: booking };
        }

        return { success: false, error: "Booking not found" };
      }
      return { success: false, error: extractApiErrorMessage(response, "Failed to fetch booking") };
    } catch (error: any) {
      return { success: false, error: extractApiErrorMessage(error, "Failed to fetch booking") };
    }
  };

export const cancelBooking =
  (bookingId: number | string, reason: string) => async (_dispatch: any) => {
    try {
      const response: any = await requestBookingCancel(bookingId, { reason });
      if (response && response.success) return { success: true, data: response.data };
      return { success: false, error: extractApiErrorMessage(response, "Failed to cancel booking") };
    } catch (error: any) {
      return { success: false, error: extractApiErrorMessage(error, "Failed to cancel booking") };
    }
  };

export const requestDateChange =
  (bookingId: number | string, payload: { new_check_in: string; new_check_out: string; message: string }) =>
  async (_dispatch: any) => {
    try {
      const response: any = await requestBookingDateChange(bookingId, payload);
      if (response && response.success) return { success: true, data: response.data };
      return { success: false, error: extractApiErrorMessage(response, "Failed to request date change") };
    } catch (error: any) {
      return { success: false, error: extractApiErrorMessage(error, "Failed to request date change") };
    }
  };

export const confirmPayAtHotel =
  (bookingId: number | string, payload: { status: 'paid' | 'refunded'; notes: string }) =>
  async (_dispatch: any) => {
    try {
      const response: any = await confirmPayAtHotelPayment(bookingId, payload);
      if (response && response.success) return { success: true, data: response.data };
      return { success: false, error: extractApiErrorMessage(response, 'Failed to confirm payment') };
    } catch (error: any) {
      return { success: false, error: extractApiErrorMessage(error, 'Failed to confirm payment') };
    }
  };

export const fetchRoomTypes = () => async (dispatch: any) => {
  try {
    dispatch(setLoading(true));

    const response: any = await getRoomTypes();

    if (response && response.success) {
      dispatch(getRoomTypesSuccess(response.data));
    } else {
      dispatch(apiError("Failed to fetch room types"));
    }
  } catch (error: any) {
    const errorMessage = extractApiErrorMessage(error, "An error occurred while fetching room types");
    dispatch(apiError(errorMessage));
  }
};

export const fetchPropertyTypes = () => async (dispatch: any) => {
  try {
    dispatch(setPropertyTypesLoading(true));

    const response: any = await getPropertyTypes();

    if (response && response.success !== false) {
      const propertyTypes = normalizePropertyTypes(response);
      dispatch(getPropertyTypesSuccess(propertyTypes));
      return { success: true, data: propertyTypes };
    }

    const errorMessage = extractApiErrorMessage(response, "Failed to fetch property types");
    dispatch(apiError(errorMessage));
    dispatch(setPropertyTypesLoading(false));
    return { success: false, error: errorMessage };
  } catch (error: any) {
    const errorMessage = extractApiErrorMessage(error, "An error occurred while fetching property types");
    dispatch(apiError(errorMessage));
    dispatch(setPropertyTypesLoading(false));
    return { success: false, error: errorMessage };
  }
};

export const fetchBedTypes = () => async (dispatch: any) => {
  try {
    dispatch(setLoading(true));

    const response: any = await getBedTypes();

    if (response && response.success !== false) {
      dispatch(getBedTypesSuccess(response.data ?? response));
      return { success: true, data: response.data ?? response };
    }

    const errorMessage = extractApiErrorMessage(response, "Failed to fetch bed types");
    dispatch(apiError(errorMessage));
    return { success: false, error: errorMessage };
  } catch (error: any) {
    const errorMessage = extractApiErrorMessage(error, "An error occurred while fetching bed types");
    dispatch(apiError(errorMessage));
    return { success: false, error: errorMessage };
  }
};

export const fetchRoomBeds =
  (roomId: number | string) => async (_dispatch: any) => {
    try {
      const response: any = await getRoomBeds(roomId);
      return { success: true, data: response };
    } catch (error: any) {
      return { success: false, error: extractApiErrorMessage(error, "Failed to fetch room beds") };
    }
  };

export const createRoomBeds =
  ({ roomId, bedsData }: any) => async (_dispatch: any) => {
    try {
      const response: any = await postVendorRoomBeds({ roomId, bedsData });
      if (response && response.success !== false) return { success: true, data: response.data ?? response };
      return { success: false, error: extractApiErrorMessage(response, "Failed to save room beds") };
    } catch (error: any) {
      return { success: false, error: extractApiErrorMessage(error, "Failed to save room beds") };
    }
  };

export const updateRoomBeds =
  ({ roomId, bedsData }: any) => async (_dispatch: any) => {
    try {
      const response: any = await putVendorRoomBeds({ roomId, bedsData });
      if (response && response.success !== false) return { success: true, data: response.data ?? response };
      return { success: false, error: extractApiErrorMessage(response, "Failed to update room beds") };
    } catch (error: any) {
      return { success: false, error: extractApiErrorMessage(error, "Failed to update room beds") };
    }
  };

export const removeRoomBeds =
  ({ roomId, bedsData }: any) => async (_dispatch: any) => {
    try {
      const response: any = await deleteVendorRoomBeds({ roomId, bedsData });
      if (response && response.success !== false) return { success: true, data: response.data ?? response };
      return { success: false, error: extractApiErrorMessage(response, "Failed to delete room beds") };
    } catch (error: any) {
      return { success: false, error: extractApiErrorMessage(error, "Failed to delete room beds") };
    }
  };

export const fetchRoomAmenities = () => async (dispatch: any) => {
  try {
    dispatch(setLoading(true));

    const response: any = await getRoomAmenities();

    if (response && response.success) {
      dispatch(getRoomAmenitiesSuccess(response.data));
    } else {
      dispatch(apiError("Failed to fetch room amenities"));
    }
  } catch (error: any) {
    const errorMessage = extractApiErrorMessage(error, "An error occurred while fetching room amenities");
    dispatch(apiError(errorMessage));
  }
};

export const createVendorProperty =
  (propertyData: any) => async (dispatch: any) => {
    try {
      dispatch(setLoading(true));

      const response: any = await postVendorProperties(propertyData);

      if (response && response.success) {
        dispatch(postVendorPropertiesSuccess(response.data));
        // Optionally refresh the dashboard after creating a property
        //dispatch(fetchVendorDashboard());
        return { success: true, data: response.data };
      } else {
        const errorMessage = extractApiErrorMessage(response, "Failed to create property");
        dispatch(apiError(errorMessage));
        return { success: false, error: errorMessage };
      }
    } catch (error: any) {
      const errorMessage = extractApiErrorMessage(error, "An error occurred while creating property");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };

export const createVendorPropertyPolicies =
  ({policiesData, propertyId}: any) => async (dispatch: any) => {
    try {
      dispatch(setLoading(true));

      const response: any = await postVendorPropertiesPolicies({policiesData, propertyId});

      if (response && response.success) {
        // FIX: Use postVendorPropertiesPoliciesSuccess instead of postVendorPropertiesSuccess
        dispatch(postVendorPropertiesPoliciesSuccess(response.data));
        return { success: true, data: response.data };
      } else {
        const errorMessage = extractApiErrorMessage(response, "Failed to create policies");
        dispatch(apiError(errorMessage));
        return { success: false, error: errorMessage };
      }
    } catch (error: any) {
      const errorMessage = extractApiErrorMessage(error, "An error occurred while creating policies");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };;

export const createVendorPropertyTaxesFees =
  ({ taxesFeesData, propertyId }: any) => async (dispatch: any) => {
    try {
      dispatch(setLoading(true));

      const response: any = await postVendorPropertyTaxesFees({ taxesFeesData, propertyId });

      if (response && response.success) {
        return { success: true, data: response.data };
      } else {
        const errorMessage = extractApiErrorMessage(response, "Failed to save taxes and fees");
        dispatch(apiError(errorMessage));
        return { success: false, error: errorMessage };
      }
    } catch (error: any) {
      const errorMessage = extractApiErrorMessage(error, "An error occurred while saving taxes and fees");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };

export const createVendorPropertyServices =
  ({ servicesData, propertyId }: any) => async (dispatch: any) => {
    try {
      dispatch(setLoading(true));

      const response: any = await postVendorPropertyServices({ servicesData, propertyId });

      if (response && response.success) {
        return { success: true, data: response.data };
      } else {
        const errorMessage = extractApiErrorMessage(response, "Failed to save guest services");
        dispatch(apiError(errorMessage));
        return { success: false, error: errorMessage };
      }
    } catch (error: any) {
      const errorMessage = extractApiErrorMessage(error, "An error occurred while saving guest services");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };

export const createVendorPropertyRoom =
  ({ roomData, propertyId }: any) => async (dispatch: any) => {
    try {
      dispatch(setLoading(true));

      const response: any = await postVendorPropertyRoom({ roomData, propertyId });

      if (response && response.success) {
        return { success: true, data: response.data, message: response?.message };
      } else {
        const errorMessage = extractApiErrorMessage(response, "Failed to create room");
        dispatch(apiError(errorMessage));
        return { success: false, error: errorMessage };
      }
    } catch (error: any) {
      const errorMessage = extractApiErrorMessage(error, "An error occurred while creating room");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };

export const createVendorPropertyRatePlan =
  ({ ratePlanData, propertyId }: any) => async (dispatch: any) => {
    try {
      dispatch(setLoading(true));

      const response: any = await postVendorPropertyRatePlan({ ratePlanData, propertyId });

      if (response && response.success !== false) {
        return { success: true, data: response.data ?? response, message: response?.message };
      }

      const errorMessage = extractApiErrorMessage(response, "Failed to create rate plan");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    } catch (error: any) {
      const errorMessage = extractApiErrorMessage(error, "An error occurred while creating rate plan");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };

export const createVendorPropertyAmenities =
  ({amenitiesData, propertyId}: any) => async (dispatch: any) => {
    try {
      dispatch(setLoading(true));

      const response: any = await postVendorPropertiesAmenities({amenitiesData, propertyId});

      if (response && response.success) {
        dispatch(postVendorPropertiesAmenitiesSuccess(response.data));
        // Optionally refresh the dashboard after creating a property
        //dispatch(fetchVendorDashboard());
        return { success: true, data: response.data };
      } else {
        const errorMessage = extractApiErrorMessage(response, "Failed to create property");
        dispatch(apiError(errorMessage));
        return { success: false, error: errorMessage };
      }
    } catch (error: any) {
      const errorMessage = extractApiErrorMessage(error, "An error occurred while creating property");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };

export const createVendorRoomAmenities =
  ({ amenitiesData, roomId }: any) => async (dispatch: any) => {
    try {
      dispatch(setLoading(true));

      const response: any = await postVendorRoomAmenities({ amenitiesData, roomId });

      if (response && response.success) {
        dispatch(postVendorRoomAmenitiesSuccess(response.data));
        return { success: true, data: response.data };
      } else {
        const errorMessage = extractApiErrorMessage(response, "Failed to create room amenities");
        dispatch(apiError(errorMessage));
        return { success: false, error: errorMessage };
      }
    } catch (error: any) {
      const errorMessage = extractApiErrorMessage(error, "An error occurred while creating room amenities");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };

export const resetVendorFlags = () => async (dispatch: any) => {
  try {
    dispatch(resetVendorFlag());
  } catch (error: any) {
    dispatch(apiError(error));
  }
};

export const resetPropertyCreationState = () => async (dispatch: any) => {
  try {
    dispatch(resetVendorFlag());
    dispatch(resetPropertyCreation());
  } catch (error: any) {
    dispatch(apiError(error));
  }
};

export const updateVendorProperty =
  (propertyData: any, propertyId: any) => async (dispatch: any) => {
    try {
      const response: any = await putVendorProperty({ propertyData, propertyId });
      if (response && response.success) {
        return { success: true, data: response.data };
      }
      const errorMessage = extractApiErrorMessage(response, "Failed to update property");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    } catch (error: any) {
      const errorMessage = extractApiErrorMessage(error, "An error occurred while updating property");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };

export const updateVendorPropertyPolicies =
  ({ policiesData, propertyId }: any) => async (dispatch: any) => {
    try {
      const response: any = await putVendorPropertiesPolicies({ policiesData, propertyId });
      if (response && response.success) {
        return { success: true, data: response.data };
      }
      const errorMessage = extractApiErrorMessage(response, "Failed to update policies");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    } catch (error: any) {
      const errorMessage = extractApiErrorMessage(error, "An error occurred while updating policies");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };

export const updateVendorPropertyAmenities =
  ({ amenitiesData, propertyId }: any) => async (dispatch: any) => {
    try {
      const response: any = await putVendorPropertiesAmenities({ amenitiesData, propertyId });
      if (response && response.success) {
        return { success: true, data: response.data, message: response?.message };
      }
      const errorMessage = extractApiErrorMessage(response, "Failed to update amenities");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    } catch (error: any) {
      const errorMessage = extractApiErrorMessage(error, "An error occurred while updating amenities");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };

export const updateVendorPropertyServices =
  ({ servicesData, propertyId }: any) => async (dispatch: any) => {
    try {
      const response: any = await putVendorPropertyServices({ servicesData, propertyId });
      if (response && response.success) {
        return { success: true, data: response.data };
      }
      const errorMessage = extractApiErrorMessage(response, "Failed to update guest services");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    } catch (error: any) {
      const errorMessage = extractApiErrorMessage(error, "An error occurred while updating guest services");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };

export const updateVendorPropertyTaxesFees =
  ({ taxesFeesData, propertyId }: any) => async (dispatch: any) => {
    try {
      const response: any = await putVendorPropertyTaxesFees({ taxesFeesData, propertyId });
      if (response && response.success) {
        return { success: true, data: response.data };
      }
      const errorMessage = extractApiErrorMessage(response, "Failed to update taxes and fees");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    } catch (error: any) {
      const errorMessage = extractApiErrorMessage(error, "An error occurred while updating taxes and fees");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };

export const updateVendorPropertyContract =
  ({ contractType, propertyId }: any) => async (dispatch: any) => {
    try {
      const response: any = await putVendorPropertyContract({ contractType, propertyId });
      if (response && response.success) {
        dispatch(getVendorPropertyContractSuccess({ propertyId, data: response.data }));
        return { success: true, data: response.data };
      }
      const errorMessage = extractApiErrorMessage(response, "Failed to update property agreement");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    } catch (error: any) {
      const errorMessage = extractApiErrorMessage(error, "An error occurred while updating property agreement");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };

export const updateVendorPropertyRoom =
  ({ roomData, propertyId, roomId }: any) => async (dispatch: any) => {
    try {
      const response: any = await putVendorPropertyRoom({ roomData, propertyId, roomId });
      if (response && response.success) {
        return { success: true, data: response.data, message: response?.message };
      }
      const errorMessage = extractApiErrorMessage(response, "Failed to update room");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    } catch (error: any) {
      const errorMessage = extractApiErrorMessage(error, "An error occurred while updating room");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };

export const updateVendorRoomAmenities =
  ({ amenitiesData, roomId }: any) => async (dispatch: any) => {
    try {
      const response: any = await putVendorRoomAmenities({ amenitiesData, roomId });
      if (response && response.success) {
        return { success: true, data: response.data, message: response?.message };
      }
      const errorMessage = extractApiErrorMessage(response, "Failed to update room amenities");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    } catch (error: any) {
      const errorMessage = extractApiErrorMessage(error, "An error occurred while updating room amenities");
      dispatch(apiError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };
